import {
  AnyObject,
  CurdDataSetConfig,
  CurrentMobile,
  FormFieldConfig,
  FormFieldType,
  Geometric,
  ItemAttrListDataSet, Position
} from '../types';
import { environment } from '@mri/environment';
import { MarkerWithLabel } from '@googlemaps/markerwithlabel';
import { CurrentMobileDetailComponent } from '@mri/app/modules';
import { PermissionKey } from '@mri/enums';
import { forkJoin, Observable, of, tap } from 'rxjs';

function getIconCommand(current: CurrentMobile): string{
  const { command } = current || {};
  if(command?.status === 'pending' ) return  '<i class="fi fi-br-paper-plane text-primary-500"></i>';
  if(command?.status === 'success' ) return  '<i class="fi fi-br-paper-plane text-success-500"></i>';
  if(command?.status === 'expired' ) return  '<i class="fi fi-br-paper-plane text-warn-500"></i>';

  return '';
}

const filterConfig: Array<FormFieldConfig> =  [
  {
    key: 'name',
    label: 'Placa',
    type: FormFieldType.STRING
  },
  {
    key: 'uniqueId',
    label: 'Imei del Dispositivo',
    type: FormFieldType.STRING,
    permission: PermissionKey.DEVICE_LIST
  },
  {
    key: 'deviceId',
    label: 'Dispositivo',
    type: FormFieldType.OPTIONS_MULTIPLE,
    optionsGateway: 'devices',
    permission: PermissionKey.DEVICE_LIST

  },
  {
    key: 'mobileId',
    label: 'Vehiculo',
    type: FormFieldType.OPTIONS_MULTIPLE,
    optionsGateway: 'mobiles',
    permission: PermissionKey.MOBILE_LIST
  },
  {
    key: 'reportStatus',
    label: 'Estado de reporte',
    type: FormFieldType.OPTIONS_MULTIPLE,
    options: [
      { label: 'Conectado', value: 'online' },
      { label: 'Desconectado', value: 'offline' },
    ]

  },
  {
    key: 'motionStatus',
    label: 'Estado de movimiento',
    type: FormFieldType.OPTIONS_MULTIPLE,
    options: [
      { label: 'Moviendose', value: 'moving' },
      { label: 'Estacionado', value: 'parked' },
    ]
  },
  {
    key: 'hasMobile',
    label: 'Tipo',
    type: FormFieldType.OPTIONS_MULTIPLE,
    options: [
      { label: 'Dispositivo', value: 'false' },
      { label: 'Vehiculo', value: 'true' },
    ]
  },
  {
    key: 'hardware',
    label: 'Hardware',
    type: FormFieldType.OPTIONS_MULTIPLE,
    optionsGateway: 'hardware',
    containerClass: 'col-md-6 col-sm-6',
    permission: PermissionKey.HARDWARE_LIST
  },
  {
    key: 'mobilePropertyId',
    label: 'Propietario del Vehiculo',
    type: FormFieldType.OPTIONS_MULTIPLE,
    optionsGateway: 'properties',
    hide: ({ auth }) =>  (auth?.propertyIds?.length || 0 ) < 2,
    permission: PermissionKey.PROPERTY_LIST
  },
  {
    key: 'devicePropertyId',
    label: 'Propietario del Dispositivo',
    type: FormFieldType.OPTIONS_MULTIPLE,
    optionsGateway: 'properties',
    hide: ({ auth }) =>  (auth?.propertyIds?.length || 0 ) < 2,
    permission: PermissionKey.PROPERTY_LIST
  }
]

function getNameWithIndicative(current: CurrentMobile): string {

  const hasIgnition = !!current.config?.sensors?.find(s => s.key === 'ignition');
  const { position, mobile, device } = current || {};

  const { sensors, deviceTime, speed } = position || {};
  const { ignition } = sensors || {};

  const time = new Date().getTime() - (new Date(deviceTime || '')?.getTime() || 0);
  const isDisconnect = time > (1000 * 60 * 10);
  const iconConnect = isDisconnect ? `<i class="fi fi-br-wifi-slash text-warning-400 fa-beat" title="Desconectado"></i>` : '';
  const iconPower = !isDisconnect && hasIgnition && ignition ? `<i class="fa-solid fa-power-off ${ignition ? 'text-success-500' : 'text-muted '}"></i>` : '';
  const iconSpeed = !isDisconnect && (speed || 0) > 2 ? '<i class="fi fi-br-tachometer-fast text-primary-500"></i>' : '';
  const iconCommand = getIconCommand(current)

  const className = isDisconnect ? 'text-warning-200' : '';

  return `<span class="${className}">${mobile?.name || device.uniqueId}</span> <label class="inditative-content">${iconPower} ${iconSpeed} ${iconConnect} ${iconCommand}</label> `.trim();
}

function convertGeoJSONToLatLng(geoJsonPoint: Geometric): google.maps.LatLng {
  const [lng, lat] = geoJsonPoint.coordinates as [number, number];
  return new google.maps.LatLng(lat, lng);
}


function animateMarker(marker: google.maps.Marker, newPosition: google.maps.LatLng, duration: number) {
  const startPosition = marker.getPosition();
  const startTime = performance.now();

  if (!startPosition) return;

  function animationStep(time: number) {
    const elapsedTime = time - startTime;
    const progress = Math.min(elapsedTime / duration, 1);

    const lat = (startPosition?.lat() || 0) + (newPosition.lat() - (startPosition?.lat() || 0)) * progress;
    const lng = (startPosition?.lng() || 0) + (newPosition.lng() - (startPosition?.lng() || 0)) * progress;

    marker.setPosition(new google.maps.LatLng(lat, lng));

    if (progress < 1) {
      requestAnimationFrame(animationStep);
    }
  }

  requestAnimationFrame(animationStep);
}

function getUrlIcon(current: CurrentMobile): string {
  return `${environment.assets['bucket']}images/vehicles/markers/${current.mobile?.icon || 'default'}.png#${current.device._id}`;
}

function rotateIconMarker(mobile: CurrentMobile) {
  const { course } = mobile.position || {};
  const myIcon = getUrlIcon(mobile);
  setTimeout(function() {
    const style = [
      'transition: 0.4s all',
      '-webkit-transform: rotate(' + course + 'deg)',
      '-moz-transform: rotate(' + course + 'deg)',
      '-ms-transform: rotate(' + course + 'deg)',
      'transform: rotate(' + course + 'deg)'
    ].join(';');

    const element = document.querySelector(`img[src="${myIcon}"]`);
    if (!element) return;

    element.setAttribute('style', style);

  }, 500);
}

function showMarkerElement(props: ItemAttrListDataSet): Observable<AnyObject> {
  const { element, component } = props;
  const markers = component?.getMapElements(element._id);

  if (markers?.length) return of(null);

  const [lng, lat] = element.position?.geometric?.coordinates || [];

  if (!lng) return of(null);

  component?.markElement(element._id, 'localizable', true);

  const marker = new MarkerWithLabel({
    title: element.mobile?.name || element.device.uniqueId,
    position: { lat: parseFloat(lat.toString()), lng: parseFloat(lng.toString()) },
    draggable: false,
    icon: {
      url: getUrlIcon(element),
      size: new google.maps.Size(40, 40),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(20, 22),
      rotation: 90
    },
    labelContent: getNameWithIndicative(element),
    labelAnchor: new google.maps.Point(28, -20),
    labelClass: `label-marker marker_${element.device._id} bg-accent-100`,
    visible: true
  });

  marker.addListener('click', () => {
    openDrawerDetail(props);
  });

  component?.addMapElements(element._id, [marker]);

  rotateIconMarker(element);

  if(!component) return of(null);

  return component.genericResourceSrv.getElements<Position>('positions', {
    filter: {
      deviceId: element.device._id,
        ...(element.mobile ? {mobileId: element.mobile._id} : {}),
      '$sort:deviceTime': 'desc'
    }
  }).pipe(
    tap( response => {
      const flightPlanCoordinates  = response.objects.map(p => ({
        lat: p.geometric.coordinates[1] as number,
        lng: p.geometric.coordinates[0] as number,
      }));

      const flightPath = new google.maps.Polyline({
        path: flightPlanCoordinates,
        strokeColor: '#3daac1',
        geodesic: true,
        strokeOpacity: 8,
        strokeWeight: 5
      });

      component?.addMapElements('flightPath', [flightPath]);

    })
  )
}

function centerElements(prop: ItemAttrListDataSet) {
  const { selectedElements, component } = prop;

  const locations = (selectedElements || []).map(element =>
    element.position?.geometric
  ).filter(g => !!g);

  if (locations.length)
    component?.centerMap(locations as Geometric[]);
}

function openDrawerDetail(prop: ItemAttrListDataSet) {
  const { element, selectedElements, component } = prop;
  const [selected] = (element ? [element] : selectedElements) || [];

  if (!selected) return;

  const { mobile, device } = selected as CurrentMobile;
  component?.openDrawer(
    mobile?.name || device.uniqueId,
    CurrentMobileDetailComponent,
    {
      elementId: selected._id
    }
  );
}

export const currentMobileDataSetConfig: CurdDataSetConfig<CurrentMobile> = {
  entityName: 'Rastreable',
  collectionName: 'Rastreables',
  gatewayUrl: 'mobiles/currents',
  baseBreadcrumb: 'trackers',
  list: {
    pageConfig: {
      title: 'Ubicacion',
      breadcrumb: [
        {
          type: 'item',
          title: 'Ubicación',
          pageName: 'Ubicación',
        }
      ]
    },
    defaultMode: 'map',
    card: {
      hasSocket: true,
      structure: {
        body: ({ element }) => {
          const info = element;
          if (!info?.position?.deviceTime) return [{ type: 'text', value: `Nunca ha reportado` }];

          return [{ type: 'time-real', value: new Date(info?.position.deviceTime) }];
        },
        img: ({ element }) => `${environment.assets['bucket']}images/vehicles/${element?.mobile?.icon || 'default'}.png`,
        title: ({ element }) => getNameWithIndicative(element)
      }
    },
    actionsKeys: [],
    extraActions: [
      {
        icon: ({
                 marks,
                 element
               }) => marks?.[element?._id || '']?.['details'] ? 'ti-eye f-22 text-primary-500 active' : 'ti-eye f-22',
        key: 'details',
        inRow: true,
        label: 'Detalles',
        disableWhen: ({ selectedElements }) => selectedElements?.length !== 1,
        click: (prop) => openDrawerDetail(prop)
      },
      {
        icon: ({
                 marks,
                 element
               }) => marks?.[element?._id || '']?.['localizable'] ? 'ti-map-pin f-22 text-primary-500 active' : 'ti-map-pin f-22',
        key: 'view',
        inRow: true,
        label: 'Mostrar',
        disableWhen: ({ selectedElements }) => selectedElements?.length === 0,
        click: (prop) => {
          const { element, selectedElements, component, marks } = prop;

          const elementList = element ? [element] : selectedElements;

          const elements$ = elementList?.map(element => {
            const mark = marks?.[element._id] || {};

            const localizable = mark['localizable'];

            if (localizable) {
              component?.removeMapElements(element._id);
              component?.markElement(element._id, 'localizable', false);

              return of(null);
            }
            return showMarkerElement({ ...prop, element });
          }).filter(v => !!v);

          if(!elements$?.length) return of(null);

          return forkJoin(elements$);
        }
      }
    ],
    filter: filterConfig,
    searchFilter: filterConfig,
    onMapReady: (prop) => {
      const { element, marks, component, elements } = prop;
      const map = component?.map;

      if (!map) return;

      const elementList = element ? [element] : elements;

      elementList?.forEach(element => {
        const mark = marks?.[element._id] || {};

        const localizable = mark['localizable'];

        if (!localizable) return;

        const [marker] = (component?.getMapElements(element._id) || []) as [MarkerWithLabel];

        const { geometric } = element?.position || {};
        if (!geometric) return;

        if (!element.position) return;

        const newPosition = convertGeoJSONToLatLng(element.position?.geometric);

        marker.set('labelContent', getNameWithIndicative(element));
        animateMarker(marker, newPosition, 200);
        rotateIconMarker(element);
      });

      centerElements(prop);
    },
    onSelectedElements: (prop) => {
      const { selectedElements } = prop;
      const elements$ = (selectedElements)?.map(element =>
        showMarkerElement({ ...prop, element })
      ).filter(v => !!v);

      centerElements(prop);

      if(!elements$?.length) return of(null);
      return forkJoin(elements$)
    },
    onDlbClickElements: prop => openDrawerDetail(prop)
  },
  pages: ['list']
};
